/**
 * Utility contact pilot (data_route 'utility_request'). For a pilot firm on
 * the pilot utility, Get data asks a named person at the utility for the
 * customer's interval data instead of asking the customer. First pilot:
 * Elephant Energy × National Grid (Massachusetts), contact Ben McArthur,
 * 2026-10-29 to 2027-01-29 (utility_data_pilots).
 *
 * The request email's Reply-To is the request's receiving address, so the
 * contact replies with the file attached and inbound-email-background loads
 * it, accepting only mail from the contact's own domain with DMARC passing
 * (ami_consent_requests.allowed_sender_domain). No customer step.
 *
 * Kept free of the parser so the send function can bundle it.
 */

export interface UtilityPilot {
  id: string;
  name: string;
  firm_id: string | null;
  utility_id: string;
  contact_name: string;
  contact_email: string;
  starts_at: string;
  ends_at: string;
  reminded_14d_at?: string | null;
  reminded_3d_at?: string | null;
}

/** True while the pilot runs: attached to a firm, started, not ended. */
export function pilotIsLive(pilot: Pick<UtilityPilot, 'firm_id' | 'starts_at' | 'ends_at'> | null | undefined, now: Date = new Date()): boolean {
  if (!pilot || !pilot.firm_id) return false;
  return new Date(pilot.starts_at) <= now && now < new Date(pilot.ends_at);
}

/** The domain of an address, lower-cased, or null. */
export function emailDomain(address: string | null | undefined): string | null {
  const value = (address ?? '').trim().toLowerCase();
  const at = value.lastIndexOf('@');
  if (at <= 0 || at === value.length - 1) return null;
  return value.slice(at + 1);
}

/** The contact's first name, for the greeting. */
export function firstName(name: string | null | undefined): string {
  return (name ?? '').trim().split(/\s+/)[0] ?? '';
}

// ── Reminders before the end ───────────────────────────────────────────────

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Which reminder to send Jake now, if any: 14 days out, then 3 days out. Each
 * goes once; a pilot found inside the 3-day window with neither sent gets only
 * the 3-day one. Nothing after the end: the pilot has already reverted.
 */
export function reminderDue(
  pilot: Pick<UtilityPilot, 'ends_at' | 'reminded_14d_at' | 'reminded_3d_at'>,
  now: Date = new Date(),
): '14d' | '3d' | null {
  const left = new Date(pilot.ends_at).getTime() - now.getTime();
  if (left <= 0) return null;
  if (left <= 3 * DAY_MS) return pilot.reminded_3d_at ? null : '3d';
  if (left <= 14 * DAY_MS) return pilot.reminded_14d_at ? null : '14d';
  return null;
}

// ── The request email ──────────────────────────────────────────────────────

export interface PilotRequestEmail {
  contactName: string;
  utilityName: string;
  companyName: string;
  contractorName: string;
  contractorEmail: string;
  customerName: string;
  serviceAddress: string;
  accountNumber: string;
  replyAddress: string;
  replyBy: string;
  /** utility_data_pilots.name, e.g. "Elephant Energy × National Grid EVSE". */
  pilotName: string;
}

function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** What we ask for, said once for both the HTML and the text body. */
export const PILOT_DATA_ASK = [
  'At least 12 months of 15-minute interval data, ending as recently as possible. More than 12 months is welcome.',
  'Demand (kW) or usage per interval (kWh), with a date and time on every reading.',
  'One CSV or Excel file, attached to your reply. Please attach the file rather than sending a link.',
];

export function buildPilotRequestEmail(p: PilotRequestEmail): { subject: string; html: string; text: string } {
  const who = p.companyName || p.contractorName || 'A TrueLoad contractor';
  const utility = p.utilityName || 'National Grid';
  const greeting = firstName(p.contactName) ? `Hi ${firstName(p.contactName)},` : 'Hello,';
  const subject = `Interval data request – ${p.customerName} – ${p.serviceAddress} – Acct ${p.accountNumber}`;

  const rows: Array<[string, string]> = [
    ['Customer', p.customerName],
    ['Service address', p.serviceAddress],
    ['Account number', p.accountNumber],
    ['Requested by', [p.contractorName, p.companyName].filter(Boolean).join(', ') || who],
  ];

  const html = `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"></head>
<body style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #111827;">
  <p style="margin-top: 0;">${escapeHtml(greeting)}</p>
  <p>${escapeHtml(who)} needs the interval data for this ${escapeHtml(utility)} customer, for an NEC 220.87 load calculation under the ${escapeHtml(p.pilotName)} pilot:</p>
  <table style="border-collapse: collapse; margin: 16px 0; font-size: 14px;">
    ${rows.map(([k, v]) => `<tr><td style="padding: 6px 12px; background: #f9fafb; font-weight: 600; border: 1px solid #e5e7eb;">${escapeHtml(k)}</td><td style="padding: 6px 12px; border: 1px solid #e5e7eb;">${escapeHtml(v)}</td></tr>`).join('\n    ')}
  </table>
  <p style="margin-bottom: 4px;"><strong>What we need</strong></p>
  <ul style="margin-top: 0; line-height: 1.6;">
    ${PILOT_DATA_ASK.map(line => `<li>${escapeHtml(line)}</li>`).join('\n    ')}
  </ul>
  <p style="margin-bottom: 4px;"><strong>How to send it</strong></p>
  <p style="margin-top: 0;">Reply to this email with the file attached. The reply goes to an address set up for this one job, <a href="mailto:${escapeHtml(p.replyAddress)}">${escapeHtml(p.replyAddress)}</a>, and the data loads into the project by itself. Please send one customer per reply, and keep the address as it is. It is open until ${escapeHtml(p.replyBy)}.</p>
  <p>Questions about the job go to ${escapeHtml(p.contractorName || who)}${p.contractorEmail ? ` at <a href="mailto:${escapeHtml(p.contractorEmail)}">${escapeHtml(p.contractorEmail)}</a>` : ''}; questions about TrueLoad to jake@ethermsolutions.com.</p>
  <p>Thank you.</p>
  <hr style="border: none; border-top: 1px solid #e5e7eb; margin: 24px 0;">
  <p style="font-size: 12px; color: #6b7280; margin: 0;">TrueLoad &middot; NEC 220.87 load calculations &middot; <a href="https://app.trueloadapp.com" style="color: #6b7280;">app.trueloadapp.com</a></p>
</body>
</html>`;

  const text = [
    greeting,
    '',
    `${who} needs the interval data for this ${utility} customer, for an NEC 220.87 load calculation under the ${p.pilotName} pilot:`,
    '',
    ...rows.map(([k, v]) => `  ${k}: ${v}`),
    '',
    'What we need',
    ...PILOT_DATA_ASK.map(line => `  - ${line}`),
    '',
    'How to send it',
    `Reply to this email with the file attached. The reply goes to an address set up for this one job, ${p.replyAddress}, and the data loads into the project by itself. Please send one customer per reply, and keep the address as it is. It is open until ${p.replyBy}.`,
    '',
    `Questions about the job go to ${p.contractorName || who}${p.contractorEmail ? ` at ${p.contractorEmail}` : ''}; questions about TrueLoad to jake@ethermsolutions.com.`,
    '',
    'Thank you.',
    '',
    '--',
    'TrueLoad · NEC 220.87 load calculations · app.trueloadapp.com',
  ].join('\n');

  return { subject, html, text };
}
